import React, { useEffect, useState } from 'react';

interface Opts {
  fallback: React.ReactNode;
}
type Unpromisify<T> = T extends Promise<infer P> ? P : never;

/** Fetch the component's file ahead of its first render (hover, idle time). */
export interface Preloadable {
  preload: () => void;
}

const RELOAD_FLAG = 'chunk-reload';
const isChunkLoadError = (e: unknown): boolean =>
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Failed to load module script/i.test(
    (e as Error)?.message ?? '',
  );

// A new deploy ships fresh content-hashed chunks. A tab still running the
// previous build that lazy-loads a now-missing chunk reloads once to pull the
// new index.html (which references the new chunks). The sessionStorage flag
// prevents an infinite reload loop when the failure is a real bug, not a stale
// chunk — that case falls through to the ErrorBoundary.
const withChunkRetry =
  <T,>(factory: () => Promise<T>) =>
  (): Promise<T> =>
    factory().then(
      mod => {
        sessionStorage.removeItem(RELOAD_FLAG);
        return mod;
      },
      (err: unknown) => {
        if (isChunkLoadError(err) && !sessionStorage.getItem(RELOAD_FLAG)) {
          sessionStorage.setItem(RELOAD_FLAG, '1');
          window.location.reload();
          return new Promise<T>(() => {});
        }
        throw err;
      },
    );

// Deliberately not React.lazy + Suspense. React holds a Suspense reveal back
// until 300 ms after its fallback was shown, so the first open of every lazy
// screen stayed blank for 300 ms even with its file already in hand. Here the
// component renders in the same tick its file arrives.
export const lazyLoad = <
  T extends Promise<any>,
  U extends React.ComponentType<any>,
>(
  importFunc: () => T,
  selectorFunc?: (s: Unpromisify<T>) => U,
  opts: Opts = { fallback: null },
): U & Preloadable => {
  let loaded: U | null = null;
  let loading: Promise<U> | null = null;
  let preloading = false;

  const keep = (module: Unpromisify<T>): U =>
    (loaded = selectorFunc ? selectorFunc(module) : (module.default as U));

  const load = (): Promise<U> => {
    if (!loading) {
      loading = withChunkRetry(() => importFunc().then(keep))();
      // A failed attempt is not final: the next mount tries again.
      loading.catch(() => (loading = null));
    }
    return loading;
  };

  const Component = (props: React.ComponentProps<U>): React.JSX.Element => {
    const [, setLoaded] = useState(loaded !== null);
    const [failure, setFailure] = useState<unknown>(null);

    useEffect(() => {
      let mounted = true;
      if (loaded) setLoaded(true);
      else
        load().then(
          () => mounted && setLoaded(true),
          (error: unknown) => mounted && setFailure(error),
        );
      return () => {
        mounted = false;
      };
    }, []);

    if (failure) throw failure;
    const Loaded = loaded as React.ComponentType<
      React.ComponentProps<U>
    > | null;
    return Loaded ? <Loaded {...props} /> : <>{opts.fallback}</>;
  };

  // Silent on failure: the attempt that matters, with its reload-once
  // handling, is the one made when the component mounts.
  Component.preload = () => {
    if (loaded || loading || preloading) return;
    preloading = true;
    importFunc()
      .then(keep, () => undefined)
      .finally(() => (preloading = false));
  };

  return Component as unknown as U & Preloadable;
};
